<template>
  <b-modal
    :visible="!!event"
    :title="titleText"
    centered
    scrollable
    @shown="syncDaySelects"
    @change="onVisibleChange"
  >
    <div class="event-form">
    <p v-if="readOnly" class="readonly-hint span-2">
      You are viewing this schedule as {{ currentUserName || 'the other party' }}. Only the party who
      set it up can change the timeblocks. Raise anything you disagree with directly with them.
    </p>
    <p v-if="isSplit" class="split-hint span-2">{{ event.splitLabel }}</p>

    <!-- Shown FIRST so users reaching for a date night see it before
         retyping the block's own days/times (which would shrink the
         block instead of splitting it). -->
    <button
      v-if="canAddVisit && !readOnly"
      type="button"
      class="btn-add-visit span-2"
      @click="addVisit"
    >+ Add a Visit (Date Night) inside this block</button>
    <label class="field span-2">
      <span>Partner</span>
      <select :disabled="readOnly" v-model="draft.partner">
        <option value="A">{{ partyAName || 'Party A' }}</option>
        <option value="B">{{ partyBName || 'Party B' }}</option>
      </select>
    </label>

    <!-- Day choices are grouped by DISPLAYED calendar row (Sun-first), so
         picking "Next week: Sunday" always means the Sunday drawn at the
         head of the next week's grid. Values carry the underlying data
         week offset; save() re-homes the event when needed. -->
    <label class="field">
      <span>Start Day</span>
      <select :disabled="readOnly" ref="startSel" :value="startDayComposite" @change="onStartComposite($event.target.value)">
        <optgroup :label="weekLabel(0)">
          <option value="-1:sun">Sunday</option>
          <option v-for="d in MON_TO_SAT" :key="`s0-${d.value}`" :value="`0:${d.value}`">{{ d.label }}</option>
        </optgroup>
        <optgroup :label="weekLabel(1)">
          <option value="0:sun">Sunday</option>
          <option v-for="d in MON_TO_SAT" :key="`s1-${d.value}`" :value="`1:${d.value}`">{{ d.label }}</option>
        </optgroup>
        <optgroup v-if="totalWeeks > 1" :label="weekLabel(2)">
          <option value="1:sun">Sunday</option>
        </optgroup>
      </select>
    </label>
    <label class="field">
      <span>End Day</span>
      <!-- One group per displayed row from this week round to the same row of
           the next cycle, so a block may end up to a full rotation after it
           starts (Every Other Weekend's weekday parent runs Sunday of Week 2
           round to Friday of Week 1). Groups past the end of the rotation are
           labelled as looping back. -->
      <select :disabled="readOnly" ref="endSel" :value="endDayComposite" @change="onEndComposite($event.target.value)">
        <optgroup v-for="g in endDayGroups" :key="`eg-${g.delta}`" :label="g.label">
          <option v-for="o in g.options" :key="`e${g.delta}-${o.value}`" :value="o.value">{{ o.label }}</option>
        </optgroup>
      </select>
    </label>

    <label class="field">
      <span>Start Time</span>
      <input :disabled="readOnly" type="time" v-model="draft.startTime" />
    </label>
    <label class="field">
      <span>End Time</span>
      <input :disabled="readOnly" type="time" v-model="draft.endTime" />
    </label>

    <fieldset class="transition-block span-2">
      <legend>Transition at the end of parenting time</legend>
      <label class="radio-row">
        <input :disabled="readOnly" type="radio" v-model="draft.transition" :value="`${draft.partner}_drops`" />
        <span>{{ partnerName(draft.partner) }} Drops Off</span>
      </label>
      <label class="radio-row">
        <input :disabled="readOnly" type="radio" v-model="draft.transition" :value="`${otherPartner}_picks`" />
        <span>{{ partnerName(otherPartner) }} Picks Up</span>
      </label>
    </fieldset>

    <label class="field span-2">
      <span>Transition Details</span>
      <textarea :disabled="readOnly"
        v-model="draft.transitionDetails"
        rows="2"
        placeholder="e.g. pickup at daycare"
      ></textarea>
    </label>
    </div>

    <template #modal-footer>
      <template v-if="readOnly">
        <span class="spacer"></span>
        <button type="button" class="btn-save" @click="cancel">Close</button>
      </template>
      <template v-else>
        <button v-if="!isNew" type="button" class="btn-delete" @click="del">Delete</button>
        <button v-else-if="isSplit" type="button" class="btn-alt" @click="editHost">Edit Block Instead</button>
        <span class="spacer"></span>
        <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
        <button type="button" class="btn-save" @click="save">{{ isSplit ? 'Save Visit' : 'Save' }}</button>
      </template>
    </template>
  </b-modal>
</template>

<script>
// Mon..Sat for the grouped day dropdowns. Sunday is rendered separately at
// the head of each group because a displayed week row runs Sun -> Sat while
// the data week runs Mon -> Sun: the Sunday shown at the top of row N belongs
// to data week N-1 (composite offset -1 in the first group, 0 in the second).
const MON_TO_SAT = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
]

import dialogs from '../../dialogs.js'
import { DAY_INDEX, MIN_PER_DAY, MIN_PER_WEEK, timeToMin } from '@/lib/parentingSchedule/weekMath'

export default {
  name: 'EditEventModal',
  mixins: [dialogs], // $confirmDialog / $alertDialog
  props: {
    // null when closed; otherwise: { event, weekIndex, eventIndex, isNew }
    event: { type: Object, default: null },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    // Total number of weeks in the rotation. Used to label the "Next week"
    // dropdown group as "First week (loops back)" when editing an event in
    // the last week of the rotation cycle.
    totalWeeks: { type: Number, default: 2 },
    // 'secondary' opens the modal as a details view: fields disabled, no
    // Save / Delete / Add Visit.
    role: { type: String, default: 'primary' },
    currentUserName: { type: String, default: '' },
  },
  data() {
    return { MON_TO_SAT, draft: this._buildDraft(this.event) }
  },
  watch: {
    event: {
      immediate: false,
      handler(v) {
        this.draft = this._buildDraft(v)
        this.$nextTick(this.syncDaySelects)
      },
    },
  },
  computed: {
    titleText() {
      if (this.readOnly) return 'Timeblock Details'
      if (this.isSplit) return 'Add Visit (Date Night)'
      if (this.isNew) return 'Add Timeblock'
      return 'Edit Event'
    },
    readOnly() { return this.role !== 'primary' },
    isNew() { return !!(this.event && this.event.isNew) },
    isSplit() { return !!(this.event && this.event.split) },
    // A visit can be carved out of an existing block that's long enough to
    // meaningfully split (over 4 hours).
    canAddVisit() {
      if (this.isNew || this.isSplit) return false
      const d = this.draft
      const start = (d.startWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[d.startDay] ?? 0) * MIN_PER_DAY + timeToMin(d.startTime)
      let end = (d.endWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[d.endDay] ?? 0) * MIN_PER_DAY + timeToMin(d.endTime)
      if (end <= start) end += MIN_PER_WEEK
      return end - start > 240
    },
    otherPartner() { return this.draft.partner === 'A' ? 'B' : 'A' },
    // Composite values combine the week-offset with the day for the grouped select.
    startDayComposite() { return `${this.draft.startWeekOffset || 0}:${this.draft.startDay}` },
    endDayComposite() { return `${this.draft.endWeekOffset || 0}:${this.draft.endDay}` },
    // End Day groups: displayed rows 0..N after the home week. Row k opens
    // with the Sunday that heads it (data week k-1) then Mon..Sat of week k.
    endDayGroups() {
      const N = Math.max(1, this.totalWeeks)
      const groups = []
      for (let k = 0; k <= N; k++) {
        groups.push({
          delta: k,
          label: this.weekLabel(k),
          options: [{ value: `${k - 1}:sun`, label: 'Sunday' }]
            .concat(MON_TO_SAT.map((d) => ({ value: `${k}:${d.value}`, label: d.label }))),
        })
      }
      return groups
    },
  },
  methods: {
    // Vue skips re-applying a <select :value> whose bound string didn't change
    // between modal states, but a re-rendered options subtree can reset the DOM
    // selection to the first option — re-apply explicitly. Without this a saved
    // event silently lands on the wrong day.
    syncDaySelects() {
      if (this.$refs.startSel) this.$refs.startSel.value = this.startDayComposite
      if (this.$refs.endSel) this.$refs.endSel.value = this.endDayComposite
    },
    // Label for the optgroup that shows displayed calendar row (weekIndex +
    // delta). Rows past the end of the rotation wrap back to the start —
    // label those so users understand the cycle loops.
    weekLabel(delta) {
      const W = this.event?.weekIndex ?? 0
      const row = W + delta
      if (row < this.totalWeeks) {
        const prefix = delta === 0 ? 'This week' : delta === 1 ? 'Next week' : 'The week after'
        return `${prefix} (Week ${row + 1})`
      }
      const wrapped = row % this.totalWeeks
      if (wrapped === 0) return 'First week (loops back to start of rotation)'
      return `Week ${wrapped + 1} (loops back)`
    },
    _buildDraft(event) {
      if (!event || !event.event) {
        return {
          partner: 'A',
          startDay: 'mon',
          endDay: 'wed',
          startTime: '00:00',
          endTime: '00:00',
          startWeekOffset: 0,
          endWeekOffset: 0,
          transition: null,
          transitionDetails: '',
        }
      }
      const e = event.event
      return {
        partner: e.partner || 'A',
        startDay: e.startDay || 'mon',
        endDay: e.endDay || 'mon',
        startTime: e.startTime || '00:00',
        endTime: e.endTime || '00:00',
        startWeekOffset: e.startWeekOffset || 0,
        endWeekOffset: e.endWeekOffset || 0,
        transition: e.transition || null,
        transitionDetails: e.transitionDetails || '',
      }
    },
    onStartComposite(v) {
      const [off, day] = v.split(':')
      this.draft.startWeekOffset = Number(off)
      this.draft.startDay = day
    },
    onEndComposite(v) {
      const [off, day] = v.split(':')
      this.draft.endWeekOffset = Number(off)
      this.draft.endDay = day
    },
    partnerName(p) {
      if (p === 'A') return this.partyAName || 'Party A'
      if (p === 'B') return this.partyBName || 'Party B'
      return ''
    },
    async save() {
      if (this.readOnly) return
      const d = { ...this.draft }
      // Flatten both endpoints to minutes relative to the home week's Monday.
      // startWeekOffset can be -1 here: the Sunday displayed at the head of
      // this week's row belongs to the previous data week.
      const startPos = (d.startWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[d.startDay] ?? 0) * MIN_PER_DAY + timeToMin(d.startTime)
      let endPos = (d.endWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[d.endDay] ?? 0) * MIN_PER_DAY + timeToMin(d.endTime)
      // Auto-correct: an end at or before the start means the block crosses
      // into the following week (e.g. Fri 3pm -> Mon 9am picked in one group).
      if (endPos <= startPos) endPos += MIN_PER_WEEK

      // A block may run for up to one full rotation (it then ends in the
      // same displayed row of the next cycle), never longer.
      if (endPos - startPos > this.totalWeeks * MIN_PER_WEEK) {
        this.$alertDialog(
          'A timeblock cannot be longer than the whole rotation.',
          { title: 'Timeblock too long' }
        )
        return
      }
      // Re-home the event into the data week where it starts, so stored
      // events always have startWeekOffset 0 and endWeekOffset 0..N (the
      // number of week boundaries the block crosses).
      const homeShift = Math.floor(startPos / MIN_PER_WEEK)   // -1, 0, or 1
      const endWeekOffset = Math.floor(endPos / MIN_PER_WEEK) - homeShift
      const W = this.event.weekIndex
      const targetWeekIndex = ((W + homeShift) % this.totalWeeks + this.totalWeeks) % this.totalWeeks

      // Guard the "meant to add a visit" trap: retyping an existing block's
      // days/times down to a short window REPLACES the block. If the edit
      // shrinks it below half its length, confirm and point to the visit
      // flow, which splits the block instead of shrinking it.
      if (!this.isNew && !this.isSplit) {
        const orig = this.event.event || {}
        const oStart = (orig.startWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[orig.startDay] ?? 0) * MIN_PER_DAY + timeToMin(orig.startTime)
        let oEnd = (orig.endWeekOffset || 0) * MIN_PER_WEEK + (DAY_INDEX[orig.endDay] ?? 0) * MIN_PER_DAY + timeToMin(orig.endTime)
        if (oEnd <= oStart) oEnd += MIN_PER_WEEK
        const oldLen = oEnd - oStart
        const newLen = endPos - startPos
        if (newLen < oldLen / 2) {
          const proceed = await this.$confirmDialog(
            'This edit shortens the timeblock to less than half its current length, and the freed time becomes unassigned.\n\n'
            + 'If you meant to give the other parent a short visit (date night) inside this block, Cancel and use "+ Add a Visit (Date Night)" instead. That splits the block around the visit.\n\nShorten the block anyway?',
            { title: 'Shorten timeblock', okTitle: 'Shorten anyway' }
          )
          if (!proceed) return
        }
      }

      this.$emit('save', {
        weekIndex: W,
        targetWeekIndex,
        eventIndex: this.event.eventIndex,
        isNew: this.isNew,
        split: this.event.split || null, // set when this save carves a visit out of a host block
        event: { ...d, startWeekOffset: 0, endWeekOffset },
      })
    },
    del() {
      if (this.readOnly) return
      this.$emit('delete', {
        weekIndex: this.event.weekIndex,
        eventIndex: this.event.eventIndex,
      })
    },
    // Escape hatch from the visit modal: back to editing the block itself.
    editHost() {
      this.$emit('edit-host', {
        weekIndex: this.event.split.weekIndex,
        eventIndex: this.event.split.eventIndex,
      })
    },
    // Switch this edit into carving a visit (date night) out of the block.
    addVisit() {
      if (this.readOnly) return
      this.$emit('add-visit', {
        weekIndex: this.event.weekIndex,
        eventIndex: this.event.eventIndex,
      })
    },
    // `visible` is a one-way prop (the parent owns the state via `event`), so
    // b-modal emits `change` rather than `hidden` when it wants to close and
    // waits for the prop to follow. Listening for `hidden` here would never
    // fire: ESC, the backdrop and the × would all leave the dialog open with
    // the parent still holding the event.
    //
    // Fires once more after save/delete, when the parent clears the event of
    // its own accord. closeEditModal() is idempotent, so that is harmless —
    // and it is what puts every dismissal path on the same route.
    onVisibleChange(visible) {
      if (!visible) this.cancel()
    },
    cancel() { this.$emit('cancel') },
  },
}
</script>

<style scoped>
.readonly-hint {
  margin: 0;
  padding: 0.5rem 0.75rem;
  background: #eef3f4;
  border-left: 3px solid #7797a1;
  border-radius: 0 6px 6px 0;
  font-size: 0.8rem;
  color: #3f6772;
}
.event-form :disabled {
  background: #f8fafc;
  color: #475569;
  cursor: default;
}
/* b-modal owns the backdrop, dialog box and header/footer chrome now. This is
   only the two-column form inside its body slot. */
.event-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}
.split-hint {
  grid-column: span 2;
  margin: 0;
  padding: 0.45rem 0.6rem;
  background: #eef3f4;
  border-left: 3px solid #3f6772;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #34555f;
}
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field.span-2 { grid-column: span 2; }
.field > span {
  font-size: 0.78rem;
  font-weight: 500;
  color: #34555f;
}
.field input,
.field select,
.field textarea {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.45rem 0.6rem;
  font-size: 0.9rem;
  font-family: inherit;
}
.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #3f6772;
}
.field textarea { resize: vertical; min-height: 4rem; }

.transition-block {
  grid-column: span 2;
  border: none;
  padding: 0;
  margin: 0;
}
.transition-block legend {
  font-size: 0.78rem;
  font-weight: 500;
  color: #34555f;
  padding: 0 0 0.4rem 0;
}
.radio-row {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-right: 1rem;
  font-size: 0.9rem;
  color: #1e293b;
  cursor: pointer;
}

.spacer { flex-grow: 1; }
.btn-cancel,
.btn-save,
.btn-delete {
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0.45rem 0.95rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}
.btn-cancel {
  background: #cbd5e1;
  color: #34555f;
  border-color: #cbd5e1;
}
.btn-cancel:hover { background: #94a3b8; }
.btn-save {
  background: #34555f;
  color: #fff;
  border-color: #34555f;
}
.btn-save:hover { background: #244047; border-color: #244047; }
.btn-delete {
  background: transparent;
  color: #64748b;
  border: 1px solid #cbd5e1;
}
.btn-delete:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fee2e2;
}
.btn-add-visit {
  grid-column: span 2;
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.btn-add-visit:hover {
  border-color: #3f6772;
  border-style: solid;
  background: #eef3f4;
  color: #34555f;
}
.btn-alt {
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0.45rem 0.95rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  background: transparent;
  color: #64748b;
  border: 1px solid #cbd5e1;
}
.btn-alt:hover {
  color: #34555f;
  border-color: #3f6772;
  background: #eef3f4;
}
.event-form :disabled { cursor: not-allowed; }
</style>
